/**
 * MonthViewCalendar Component
 *
 * A reusable month-view calendar component with the following features:
 * - Displays full month grid showing all days of current month
 * - Navigation limited to current month or earlier (no future months)
 * - Each day cell is individually clickable/toggleable
 * - Shows days from current month and prior months up to today
 * - Single view (month view only)
 * - For current month, only shows days up to today (future days hidden)
 */

import { ChevronLeft, ChevronRight } from "lucide-react"
import { useCallback, useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib"

// ============================================================================
// Types
// ============================================================================

export interface CalendarDate {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  isPast: boolean
  isFuture: boolean
  day: number
}

export interface MonthViewCalendarProps {
  /**
   * Currently displayed month (controlled)
   */
  currentMonth?: Date

  /**
   * Initially displayed month (uncontrolled)
   * @default new Date() (today)
   */
  defaultMonth?: Date

  /**
   * Maximum allowed month (navigation cannot go beyond this)
   * @default new Date() (current month - no future navigation)
   */
  maxMonth?: Date

  /**
   * Minimum allowed month
   * @default undefined (no minimum)
   */
  minMonth?: Date

  /**
   * Currently selected dates
   */
  selectedDates?: Date[]

  /**
   * Default selected dates
   */
  defaultSelectedDates?: Date[]

  /**
   * Called when a date is selected/deselected
   */
  onDateSelect?: (date: Date, isSelected: boolean) => void

  /**
   * Called when month changes
   */
  onMonthChange?: (month: Date) => void

  /**
   * Disable weekend days (Saturday, Sunday)
   * @default false
   */
  disableWeekends?: boolean

  /**
   * Disable specific dates
   */
  disabledDates?: Date[]

  /**
   * Whether the calendar is readonly (user cannot select/deselect dates)
   * @default false
   */
  readonly?: boolean

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Month names
   */
  monthNames?: string[]

  /**
   * Format for month/year display
   * @default "MMMM yyyy"
   */
  monthYearFormat?: string

  /**
   * Whether to hide future days in current month
   * @default true
   */
  hideFutureDays?: boolean

  /**
   * Whether to grey out future days instead of hiding them
   * @default false
   */
  greyOutFutureDays?: boolean

  /**
   * Locale for weekday labels
   * @default undefined (uses browser default)
   */
  locale?: string
}

// ============================================================================
// Utilities
// ============================================================================

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

const isSameMonth = (date1: Date, date2: Date): boolean => {
  return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()
}

const isPast = (date: Date): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const compareDate = new Date(date)
  compareDate.setHours(0, 0, 0, 0)
  return compareDate < today
}

const isFuture = (date: Date): boolean => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  const compareDate = new Date(date)
  compareDate.setHours(23, 59, 59, 999)
  return compareDate > today
}

const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate()
}

const getMonthData = (year: number, month: number): CalendarDate[] => {
  const daysInMonth = getDaysInMonth(year, month)
  const today = new Date()

  const days: CalendarDate[] = []

  // Add only days from current month (no padding days from previous/next months)
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day)
    days.push({
      date,
      isCurrentMonth: true,
      isToday: isSameDay(date, today),
      isPast: isPast(date),
      isFuture: isFuture(date),
      day,
    })
  }

  return days
}

const formatMonthYear = (date: Date, format: string, monthNames?: string[]): string => {
  const monthIndex = date.getMonth()
  const year = date.getFullYear()

  const months = monthNames || [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  // Process format string token-by-token to avoid replacing characters in already-replaced text
  const tokens = ["MMMM", "MMM", "MM", "M", "yyyy", "yy"]
  const replacements: Record<string, string> = {
    MMMM: months[monthIndex],
    MMM: months[monthIndex].slice(0, 3),
    MM: String(monthIndex + 1).padStart(2, "0"),
    M: String(monthIndex + 1),
    yyyy: String(year),
    yy: String(year).slice(-2),
  }

  let result = ""
  let i = 0

  while (i < format.length) {
    let replaced = false
    // Try to match the longest token first
    for (const token of tokens) {
      if (format.startsWith(token, i)) {
        result += replacements[token]
        i += token.length
        replaced = true
        break
      }
    }
    // If no token matched, keep the character as-is
    if (!replaced) {
      result += format[i]
      i++
    }
  }

  return result
}

// ============================================================================
// Component
// ============================================================================

export function MonthViewCalendar({
  currentMonth: controlledMonth,
  defaultMonth = new Date(),
  maxMonth = new Date(),
  minMonth,
  selectedDates: controlledSelectedDates,
  defaultSelectedDates = [],
  onDateSelect,
  onMonthChange,
  disableWeekends = false,
  disabledDates = [],
  readonly = false,
  className,
  monthNames,
  monthYearFormat = "MMMM yyyy",
  hideFutureDays = true,
  greyOutFutureDays = false,
  locale,
}: MonthViewCalendarProps) {
  // Internal state for uncontrolled component
  const [internalMonth, setInternalMonth] = useState(defaultMonth)
  const [internalSelectedDates, setInternalSelectedDates] = useState<Date[]>(defaultSelectedDates)

  // Use controlled or uncontrolled state
  const currentMonth = controlledMonth ?? internalMonth
  const selectedDates = controlledSelectedDates ?? internalSelectedDates

  // Check if currently viewing the current month
  const today = new Date()
  const isViewingCurrentMonth = isSameMonth(currentMonth, today)

  // Check if date is selected
  const isDateSelected = useCallback(
    (date: Date) => {
      return selectedDates.some(selectedDate => isSameDay(date, selectedDate))
    },
    [selectedDates],
  )

  // Check if date is disabled
  const isDateDisabled = useCallback(
    (date: Date) => {
      // Check weekends
      if (disableWeekends) {
        const dayOfWeek = date.getDay()
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          return true
        }
      }

      // Check specific disabled dates
      if (disabledDates.length > 0) {
        return disabledDates.some(disabledDate => isSameDay(date, disabledDate))
      }

      // Future dates are disabled
      return isFuture(date)
    },
    [disableWeekends, disabledDates],
  )

  // Check if date should be greyed out (future day in current month)
  const shouldGreyOutDate = useCallback(
    (calendarDate: CalendarDate) => {
      if (!greyOutFutureDays) {
        return false
      }
      // Grey out future days when viewing current month
      return isViewingCurrentMonth && calendarDate.isFuture
    },
    [greyOutFutureDays, isViewingCurrentMonth],
  )

  // Handle date selection
  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date) || readonly) {
      return
    }

    const isSelected = isDateSelected(date)
    let newSelectedDates: Date[]

    if (isSelected) {
      // Deselect
      newSelectedDates = selectedDates.filter(d => !isSameDay(d, date))
    } else {
      // Select
      newSelectedDates = [...selectedDates, date]
    }

    // Update internal state if uncontrolled
    if (!controlledSelectedDates) {
      setInternalSelectedDates(newSelectedDates)
    }

    // Call callback
    onDateSelect?.(date, !isSelected)
  }

  // Navigate to previous month
  const handlePreviousMonth = () => {
    // Build new month date using year/month - 1 with day=1 to avoid overflow issues
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)

    // Check min month constraint (normalize to first day of month for comparison)
    const normalizedMinMonth = minMonth
      ? new Date(minMonth.getFullYear(), minMonth.getMonth(), 1)
      : null
    if (normalizedMinMonth && newMonth < normalizedMinMonth) {
      return
    }

    // Update internal state if uncontrolled
    if (!controlledMonth) {
      setInternalMonth(newMonth)
    }

    onMonthChange?.(newMonth)
  }

  // Navigate to next month
  const handleNextMonth = () => {
    // Build new month date using year/month + 1 with day=1 to avoid overflow issues
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)

    // Check max month constraint (normalize to first day of month for comparison)
    const normalizedMaxMonth = new Date(maxMonth.getFullYear(), maxMonth.getMonth(), 1)
    if (newMonth > normalizedMaxMonth) {
      return
    }

    // Update internal state if uncontrolled
    if (!controlledMonth) {
      setInternalMonth(newMonth)
    }

    onMonthChange?.(newMonth)
  }

  // Check if previous month is available (normalize dates to first day of month for comparison)
  const canGoPrevious =
    !minMonth ||
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1) >=
      new Date(minMonth.getFullYear(), minMonth.getMonth(), 1)

  // Check if next month is available (normalize dates to first day of month for comparison)
  const canGoNext =
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <=
    new Date(maxMonth.getFullYear(), maxMonth.getMonth(), 1)

  // Get month data — filter future days for current month to avoid empty grid placeholders
  const allMonthData = getMonthData(currentMonth.getFullYear(), currentMonth.getMonth())
  const visibleData =
    hideFutureDays && isViewingCurrentMonth ? allMonthData.filter(d => !d.isFuture) : allMonthData

  return (
    <div className={cn("month-view-calendar", className)}>
      {/* Header with month navigation */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm font-semibold leading-6">
          Your Progress ({selectedDates.length})
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-sm text-primary leading-6">
            {formatMonthYear(currentMonth, monthYearFormat, monthNames)}
          </span>

          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePreviousMonth}
              disabled={!canGoPrevious}
              className="w-6 h-6 p-0"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-6! w-6!" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextMonth}
              disabled={!canGoNext}
              className="w-6 h-6 p-0"
              aria-label="Next month"
            >
              <ChevronRight className="h-6! w-6!" />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 h-fit">
        {visibleData.map((calendarDate, index) => {
          const isDisabled = isDateDisabled(calendarDate.date)
          const isSelected = isDateSelected(calendarDate.date)
          const greyOut = shouldGreyOutDate(calendarDate)

          return (
            <button
              key={index}
              type="button"
              disabled={isDisabled}
              onClick={() => handleDateClick(calendarDate.date)}
              aria-pressed={isSelected}
              aria-label={calendarDate.date.toLocaleDateString(locale || "en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
              className={cn(
                "aspect-square flex items-center justify-center text-sm rounded-[10px] w-[46px] h-[50px] px-[13px] pt-1.5 pb-[7px] transition-all",
                // Base styles
                "border border-[#F2F0EB1A] bg-[#272727]",
                // Selected state
                isSelected && "bg-brand-olive text-white border-brand-olive",
                // Greyed out future days
                greyOut && "opacity-20 cursor-not-allowed",
                // Disabled state
                isDisabled && !greyOut && "opacity-30 cursor-not-allowed",
                // Hover state for enabled, non-selected buttons
                !isDisabled &&
                  !isSelected &&
                  !greyOut &&
                  "hover:bg-brand-white-alpha-10 hover:text-primary",
                // Default text colors
                !isSelected && !isDisabled && !greyOut && "text-primary",
              )}
            >
              <div className="flex flex-col gap-0.5 items-center leading-tight">
                <span className="text-[10px] leading-3.75 opacity-70">
                  {new Intl.DateTimeFormat(locale, { weekday: "short" }).format(calendarDate.date)}
                </span>
                <span className="text-sm font-semibold">{calendarDate.day}</span>
              </div>
            </button>
          )
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-5 mt-5">
        <div className="flex items-center gap-1.25">
          <div className="w-2.5 h-2.5 leading-5 rounded-[3px] bg-brand-olive border border-brand-olive" />
          <span className="text-xs text-[#F2F0EB80]">Completed</span>
        </div>
        <div className="flex items-center gap-1.25">
          <div className="w-2.5 h-2.5 rounded-[3px] bg-[#272727] border border-[#F2F0EB1A]" />
          <span className="text-xs leading-5 text-[#F2F0EB80]">Not completed</span>
        </div>
      </div>
    </div>
  )
}
